import styled, { css, useTheme } from 'styled-components';

// types
import type {
  AccordionWrapperProps,
  OkvTileWrapperProps,
  CatalogWrapperProps,
  OkvTilesWrapperProps,
} from './online-catalog-assets.types';

// utils
import { useKolo } from 'utils/hooks/use-kolo';
import { useThemedBackgroundColor } from './useThemedBackgroundColor';

const isWhiteBackground = (color: string) => color === 'white';

export const CatalogWrapper = styled.div(
  ({ backgroundColor, isAccordion }: CatalogWrapperProps) => {
    const theme = useTheme();
    const themedBackgroundColor = useThemedBackgroundColor(backgroundColor);
    const hasBackground = !isAccordion && !isWhiteBackground(backgroundColor);

    return css`
      margin: ${hasBackground ? '0' : '4rem 0'};
      padding-top: ${hasBackground ? '4rem' : '0'};

      background-color: ${hasBackground ? themedBackgroundColor : theme.colors.white};

      h2 {
        margin-bottom: 0.625rem;
        @media (${theme.device.medium}) {
          margin-bottom: 1.5rem;
        }
      }

      @media (${theme.device.large}) {
        margin: ${hasBackground ? '0' : '5rem 0'};
        padding-top: ${hasBackground ? '5rem' : '0'};
      }

      .copy-text {
        margin-bottom: 1.5rem;
        font-size: 1rem;
        line-height: 1.5rem;
      }
    `;
  },
);

export const AccordionWrapper = styled.div(
  ({ backgroundColor, isDesktop }: AccordionWrapperProps) => {
    const themedBackgroundColor = useThemedBackgroundColor(backgroundColor);

    return css`
      [data-preview-id='#st_content'] {
        div[height] {
          display: none;
        }
        &:not(div[height]) {
          width: 100%;
        }
        .items {
          margin: 0 unset;
          background-color: ${themedBackgroundColor};
        }
      }
    `;
  },
);

export const OkvTilesWrapper = styled.div(
  ({ isCarousel, backgroundColor }: OkvTilesWrapperProps) => {
    const theme = useTheme();

    return css`
      .row {
        padding-top: 2.5rem;
        padding-bottom: ${isCarousel ? '4.25rem' : '6rem'};
        align-items: stretch;

        & > div {
          margin-top: 1.5rem;
          width: 100%;
        }
      }

      .loader {
        height: 100%;
        min-height: 10rem;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fff;
        padding: 0.9375rem 0.625rem 4rem;
        border: 1px solid ${theme.colors[isWhiteBackground(backgroundColor) ? 'grey09' : 'grey05']};
      }

      .tile {
        height: inherit;
        max-width: 100%;
      }

      .carousel {
        padding: 0 0.5rem;
        height: 100%;
      }

      @media (max-width: ${theme.breakpoints[2]}) {
        .row {
          padding-bottom: ${isCarousel ? '3.25rem' : '4rem'};
        }
      }

      @media (max-width: ${theme.breakpoints[1]}) {
        .col > div > div:first-child {
          margin-bottom: 1.25rem;
        }
      }
    `;
  },
);

export const OkvTileWrapper = styled.div(
  ({ backgroundColor, type, isAccordion }: OkvTileWrapperProps) => {
    const theme = useTheme();
    const isSeries = type === 'series';
    const isKolo = useKolo();

    return css`
      display: block;
      position: relative;
      height: 100%;
      width: 100%;
      box-sizing: border-box;
      overflow: hidden;
      align-self: stretch;

      a {
        display: block;
        height: inherit;
        width: inherit;
        color: #000000;
        background-color: ${theme.colors.white};
        border: 1px solid ${theme.colors[isWhiteBackground(backgroundColor) ? 'grey09' : 'grey05']};
        border-bottom: ${!isWhiteBackground(backgroundColor) ? 'rgba(0, 0, 0, 0.04)' : 'inital'};

        &:focus {
          outline: 1px solid ${theme.colors.primaryLight};
          border: 1px solid ${theme.colors.primaryLight};
        }

        &:active {
          border: 1px solid ${theme.colors.primaryLight};
        }

        &:hover,
        &:active {
          .content {
            background-color: rgba(0, 0, 0, 0.04);
          }
        }

        &:hover,
        &:active,
        &:focus {
          text-decoration: none;

          .thumbnails {
            opacity: 1;
          }
        }
      }

      .thumbnails {
        position: absolute;
        top: 0.625rem;
        left: 0.625rem;
        width: 1.875rem;
        opacity: 0;
      }

      .content {
        padding: 1.5rem 1.5rem 3.75rem;
        display: flex;
        width: 100%;
        flex-direction: column;
        height: 100%;
      }

      .name {
        font-size: ${isKolo ? '1rem' : '1.125rem'};
        line-height: 1.5rem;

        ${isKolo &&
        css`
          font-weight: ${theme.fontWeights.bold};
        `}

        &.xy-name {
          font-size: 1.125rem;
          line-height: 1.5rem;
          font-weight: 400;
        }
      }

      .subtitle,
      .amount {
        color: #666;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .amount {
        margin-top: 1rem;
        position: absolute;
        bottom: 1.5rem;
        &.xy-text {
          font-size: 0.875rem;
          line-height: 1.42rem;
          font-weight: 400;
        }
      }

      .xy-subtitle {
        font-size: 0.875rem;
        line-height: 1.42rem;
        font-weight: 400;
      }

      .label {
        position: absolute;
        top: 1.875rem;
        right: 0;
        background-color: ${theme.colors.primaryDark};
        font-size: 0.8125rem;
        font-weight: ${theme.fontWeights.bold};
        text-transform: uppercase;
        color: ${theme.colors.white};
        line-height: 1;
        padding: 0.5rem 1.25rem 0.5rem 0.625rem;
      }

      .image {
        display: flex;
        justify-content: center;
        padding: ${!isSeries && '1rem'} 0;
        height: 11.5rem;
        overflow: hidden;
        align-items: center;
        width: 100%;

        @media (${theme.device.medium}) {
          height: ${isAccordion ? '10.5rem' : '12.5rem'};
        }

        @media (${theme.device.large}) {
          height: ${isAccordion ? '12.5rem' : '13.75rem'};
        }

        @media (${theme.device.xlarge}) {
          height: 15.75rem;
        }

        img {
          width: ${isSeries ? 'inherit' : '9.5rem'};
          height: ${isSeries ? '100%' : 'unset'};
          object-fit: cover;

          @media (${theme.device.xlarge}) {
            ${!isSeries &&
            css`
              width: 10rem;
            `}
          }
        }
      }
    `;
  },
);
